import { Container, Graphics } from "@pixi/react";
import { ColorOverlayFilter } from "@pixi/filter-color-overlay";
import { ParkPlaceBorder, PlaceFillWhite } from "./SpriteIndex";
import BoundingBox from "../../../Domain/Types/BoundingBox.type";
import { ToolSelection } from "../../Views/CreateFloorPlanView/CreateFloorPlanView";
import { PlaceVariant } from "../../../Domain/Types/FloorPlan/PlaceVariant.type";
import { drawPlaceColor } from "../functions/drawPlaceColor.function";
import { FavoriteStarGraphic, FAVORITE_STAR_OUTER_BASE } from "../../FavoriteStarGraphic";
import { TintedPlaceCircleSprite, TintedPlaceRectSprite } from "./PlaceSpriteTintedBody";
import { drawBlockedProhibitionMark } from "./drawBlockedProhibitionMark.function";

interface Props {
  placeTypeId: number;
  boundingBox: BoundingBox;
  filterGroup: ColorOverlayFilter[];
  variantColor: number;
  variant: PlaceVariant;
  placeScale: number;
  isSelectable: boolean;
  isHighlighted?: boolean;
  isFavorite?: boolean;
  colorOverlay: number | undefined;
  compatibilityFill?: number;
  tool?: ToolSelection;
  icon?: string;
  isColleaguesPlace?: boolean;
  isGrpPreferred: boolean;
}

/**
 * draw the place sprite, shared for the parking place and electric charging station
 * by pixi-sprite or pixi-graphic according to module(booking/creating)
 * @version 1.0.0
 */
export function ParkPlaceSprite({
  placeTypeId,
  boundingBox,
  filterGroup,
  variantColor,
  variant,
  placeScale,
  isSelectable,
  isHighlighted,
  isFavorite = false,
  colorOverlay,
  compatibilityFill,
  tool,
  isColleaguesPlace,
  isGrpPreferred
}: Readonly<Props>) {
  const isBlocked = variant === PlaceVariant.BLOCKED;
  const { width = 160, height = 80 } = boundingBox;

  const favoriteStarScale = Math.min(0.85, Math.max(0.38, placeScale * 0.12));
  const wS = width * placeScale;
  const hS = height * placeScale;
  const starOuter = FAVORITE_STAR_OUTER_BASE * favoriteStarScale;
  const inset = 5 * placeScale;
  const favoriteStarPosition = {
    x: wS / 2 - inset - starOuter / 2,
    y: -hS / 2 + inset + starOuter / 2
  };

  const circleRadius = width * placeScale * 0.2;
  const lineWidth = width * placeScale * 0.02;

  const { rectColor, circColor } = drawPlaceColor({
    isHighlighted: isHighlighted ?? false,
    colorOverlay,
    variantColor,
    tool,
    circleColorOverride: compatibilityFill
  });

  return (
    <>
      {isGrpPreferred ? (
        <ParkPlaceSpriteGraphic
          isSelectable={isSelectable}
          width={width}
          height={height}
          placeTypeId={placeTypeId}
          placeScale={placeScale}
          filterGroup={filterGroup}
          rectColor={rectColor}
          lineWidth={lineWidth}
          circColor={circColor}
          circleRadius={circleRadius}
          isColleaguesPlace={isColleaguesPlace}
          isBlocked={isBlocked}
        />
      ) : (
        <ParkPlaceSpriteSvgImg
          isSelectable={isSelectable}
          width={width}
          height={height}
          placeTypeId={placeTypeId}
          placeScale={placeScale}
          filterGroup={filterGroup}
          rectColor={rectColor}
          circColor={circColor}
          circleRadius={circleRadius}
          isColleaguesPlace={isColleaguesPlace}
          isBlocked={isBlocked}
        />
      )}
      {isFavorite && (
        <Container position={favoriteStarPosition}>
          <FavoriteStarGraphic starScale={favoriteStarScale} />
        </Container>
      )}
    </>
  );
}

export function borderColorPerType(placeTypeId: number) {
  const parkingPlaceBorder = 0x0000ff;
  const electricChargingBorder = 0x75baff;

  switch (placeTypeId) {
    case 2:
      return parkingPlaceBorder;
    case 4:
      return electricChargingBorder;
    default:
      return parkingPlaceBorder;
  }
}

export function spriteBorderColorPerType(placeTypeId: number) {
  switch (placeTypeId) {
    case 4:
      return ParkPlaceBorder.ElectricCharging;
    default:
      return ParkPlaceBorder.Parking;
  }
}

/** Tinted fill + hole-cut border + tinted status circle (all views except Firefox). */
export function ParkPlaceSpriteSvgImg({
  isSelectable,
  width,
  height,
  placeTypeId,
  placeScale,
  filterGroup,
  rectColor,
  circColor,
  circleRadius,
  isColleaguesPlace,
  isBlocked = false
}: Readonly<{
  isSelectable: boolean;
  width: number;
  height: number;
  placeTypeId: number;
  placeScale: number;
  filterGroup: ColorOverlayFilter[];
  rectColor: number;
  circColor: number;
  circleRadius: number;
  isColleaguesPlace?: boolean;
  isBlocked?: boolean;
}>) {
  const scaledW = width * placeScale;
  const scaledH = height * placeScale;
  const rectX = 0 - scaledW / 2;
  const rectY = 0 - scaledH / 2;
  const circleY = 0 - scaledH / 4;
  const diameter = circleRadius * 2 * (isColleaguesPlace ? 2.2 : 1);
  const circleTint = isColleaguesPlace ? 0xff0000 : circColor;
  const lineWidth = width * placeScale * 0.02;
  const markRadius = circleRadius * (isColleaguesPlace ? 2.2 : 1);

  return (
    <>
      <TintedPlaceRectSprite
        testId="parkingplace-spr-rect"
        image={PlaceFillWhite}
        tint={rectColor}
        x={rectX}
        y={rectY}
        width={scaledW}
        height={scaledH}
        isSelectable={isSelectable}
        filterGroup={filterGroup}
      />
      <TintedPlaceRectSprite
        testId="parkingplace-spr-border"
        image={spriteBorderColorPerType(placeTypeId)}
        tint={0xffffff}
        x={rectX}
        y={rectY}
        width={scaledW}
        height={scaledH}
        isSelectable={isSelectable}
        filterGroup={filterGroup}
      />
      {isBlocked ? (
        <Graphics
          data-testid="parkingplace-spr-circ-blocked"
          cursor={isSelectable ? "pointer" : "auto"}
          position={{ x: 0, y: circleY }}
          filters={filterGroup}
          draw={g => {
            drawBlockedProhibitionMark(g, markRadius, lineWidth);
          }}
        />
      ) : (
        <TintedPlaceCircleSprite
          testId={isColleaguesPlace ? "parkingplace-colleagues-spr-circ" : "parkingplace-spr-circ"}
          tint={circleTint}
          diameter={diameter}
          x={0}
          y={circleY}
          isSelectable={isSelectable}
          filterGroup={filterGroup}
        />
      )}
    </>
  );
}

/** draw parkplace sprite by PIXI.Graphics when graphic repreferred(firefox) */
export function ParkPlaceSpriteGraphic({
  isSelectable,
  width,
  height,
  placeTypeId,
  placeScale,
  filterGroup,
  rectColor,
  lineWidth,
  circColor,
  circleRadius,
  isColleaguesPlace,
  isBlocked = false
}: Readonly<{
  isSelectable: boolean;
  width: number;
  height: number;
  placeScale: number;
  placeTypeId: number;
  filterGroup: ColorOverlayFilter[];
  rectColor: number;
  lineWidth: number;
  circColor: number;
  circleRadius: number;
  isColleaguesPlace?: boolean;
  isBlocked?: boolean;
}>) {
  return (
    <>
      {/* Graphic objects, rectangle of parking places */}
      <Graphics
        data-testid="parkingplace-grp-rect"
        cursor={isSelectable ? "pointer" : "auto"}
        filters={filterGroup}
        position={{ x: 0 - (width * placeScale) / 2, y: 0 - (height * placeScale) / 2 }}
        draw={g => {
          g.clear();
          g.lineStyle(5 * placeScale, borderColorPerType(placeTypeId));
          g.beginFill(rectColor, 1); // prev: 0xd2dae1
          g.drawRect(0, 0, width * placeScale, height * placeScale);
          g.endFill();
        }}
      />
      {/* Graphic objects, circle of parking places */}
      {isBlocked ? (
        <Graphics
          data-testid="parkingplace-grp-circ-blocked"
          cursor={isSelectable ? "pointer" : "auto"}
          position={{ x: 0, y: 0 - (height * placeScale) / 4 }}
          filters={filterGroup}
          draw={g => {
            drawBlockedProhibitionMark(g, circleRadius * (isColleaguesPlace ? 2.2 : 1), lineWidth);
          }}
        />
      ) : isColleaguesPlace ? (
        <Graphics
          data-testid="workplace-grp-circ"
          cursor={isSelectable ? "pointer" : "auto"}
          position={{ x: 0, y: 0 - (height * placeScale) / 4 }}
          filters={filterGroup}
          draw={g => {
            g.clear();
            g.lineStyle(lineWidth, 0xffffff); // prev: 6 * placeScale
            g.beginFill(0xff0000, 1);
            g.drawCircle(0, 0, circleRadius * 2.2); // prev : 30 * placeScale
            g.endFill();
          }}
        />
      ) : (
        <Graphics
          data-testid="parkingplace-grp-circ"
          cursor={isSelectable ? "pointer" : "auto"}
          filters={filterGroup}
          position={{ x: 0, y: 0 - (height * placeScale) / 4 }}
          draw={g => {
            g.clear();
            g.lineStyle(lineWidth, 0xffffff);
            g.beginFill(circColor, 1); // prev: variantColor
            g.drawCircle(0, 0, circleRadius);
            g.endFill();
          }}
        />
      )}
    </>
  );
}
